import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { ShieldAlert, WifiOff } from 'lucide-react';
import type { PermissionCode } from '@fas/types';
import { Button, Spinner } from '@fas/ui';
import { OfflineAttendance } from '@/features/attendance/OfflineAttendance';
import { useAuthStore } from '@/lib/auth-store';
import { hasAny } from '@/lib/permissions';
import { retrySession } from '@/lib/session';

export function FullPageSpinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="grid min-h-dvh place-items-center">
      <Spinner label={label} />
    </div>
  );
}

/** Signed in, but the server cannot be reached to restore the session (§57, §63). */
export function OfflineScreen() {
  return (
    <main className="mx-auto mt-20 max-w-md px-4 text-center">
      <WifiOff className="mx-auto size-10 text-amber-600" aria-hidden />
      <h1 className="mt-3 text-xl font-semibold">You’re offline</h1>
      <p className="mt-2 text-sm text-slate-600">
        The app cannot reach the attendance server. It reconnects automatically when your connection returns; nothing reaches the server until then.
      </p>
      <Button className="mt-4" variant="outline" onClick={() => void retrySession()}>
        Try again
      </Button>
      <OfflineAttendance />
    </main>
  );
}

/** Redirects anonymous users to sign-in, remembering where they were going. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const status = useAuthStore((s) => s.status);
  const setupRequired = useAuthStore((s) => s.me?.mfaSetupRequired === true);
  const location = useLocation();
  if (status === 'loading') return <FullPageSpinner label="Restoring your session" />;
  if (status === 'offline') return <OfflineScreen />;
  if (status === 'anonymous') return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  // A role requires two-step sign-in: the session has no permissions until it is set up (ADR-068).
  if (setupRequired && location.pathname !== '/account') return <Navigate to="/account#two-step" replace />;
  return <>{children}</>;
}

/** Sign-in pages redirect away when a session already exists. */
export function PublicOnly({ children }: { children: ReactNode }) {
  const status = useAuthStore((s) => s.status);
  if (status === 'loading') return <FullPageSpinner />;
  if (status === 'offline') return <OfflineScreen />;
  if (status === 'authenticated') return <Navigate to="/" replace />;
  return <>{children}</>;
}

export function Forbidden() {
  return (
    <div className="mx-auto mt-16 max-w-md text-center">
      <ShieldAlert className="mx-auto size-10 text-amber-600" aria-hidden />
      <h1 className="mt-3 text-xl font-semibold">Access restricted</h1>
      <p className="mt-2 text-sm text-slate-600">
        Your role does not include permission to open this page. Contact your administrator if you believe this is a mistake.
      </p>
    </div>
  );
}

/** UI-level permission gate (the API independently enforces authorization). */
export function RequirePermission({ any, children }: { any: PermissionCode[]; children: ReactNode }) {
  const permissions = useAuthStore((s) => s.me?.permissions);
  return hasAny(permissions, any) ? <>{children}</> : <Forbidden />;
}
